import {
	forwardRef,
	type HTMLAttributes,
	useEffect,
	useCallback,
	useRef,
} from 'react'
import { cn } from '#app/utils/tailwind-merge.ts'

const Ol = forwardRef<HTMLOListElement, HTMLAttributes<HTMLOListElement>>(
	({ className, children, ...props }, forwardedRef) => {
		// Use a mutable object to store the current node
		const internalRef = useRef<{ current: HTMLOListElement | null }>({
			current: null,
		})

		// Callback ref to set the internal reference
		const setRef = useCallback(
			(node: HTMLOListElement | null) => {
				// Update internal reference
				internalRef.current.current = node
				// Handle forwarded ref (supports callback and object refs)
				if (typeof forwardedRef === `function`) {
					forwardedRef(node)
				} else if (forwardedRef) {
					forwardedRef.current = node
				}
			},
			[forwardedRef],
		)

		useEffect(() => {
			const updateListMarkerFontSizes = () => {
				const olElement = internalRef.current.current
				if (!olElement) return

				// Select all direct <li> children
				const listItems = olElement.querySelectorAll(`:scope > li`)
				listItems.forEach((li) => {
					const listItem = li as HTMLLIElement
					// Get the first direct child element
					const firstChild = listItem.firstElementChild as HTMLElement | null
					if (firstChild) {
						// Get computed font-size of the first child
						const childFontSize = window.getComputedStyle(firstChild).fontSize
						// Set custom property for marker font-size
						listItem.style.setProperty(`--marker-font-size`, childFontSize)
					} else {
						// Fallback for <li> with no element child (e.g., text only)
						listItem.style.setProperty(`--marker-font-size`, `1rem`)
					}
				})
			}

			// Run on mount and when children change
			updateListMarkerFontSizes()

			// Observe changes to the <ol> subtree for dynamic content
			const observer = new MutationObserver(updateListMarkerFontSizes)
			if (internalRef.current.current) {
				observer.observe(internalRef.current.current, {
					childList: true,
					subtree: true,
				})
			}

			// Cleanup observer on unmount
			return () => observer.disconnect()
		}, [children])

		return (
			<ol ref={setRef} className={cn(`ol`, className)} {...props}>
				{children}
			</ol>
		)
	},
)

Ol.displayName = `Ol`

export { Ol }
